import { useState } from 'react';
import { Link } from 'react-router';
import { api, ApiError } from '../api/client.js';
import { Field, Modal, ReasonModal, Spinner, StatusBadge } from './ui.jsx';
import { StatTile } from './charts.jsx';
import { useFetch } from '../lib/hooks.js';
import { formatDate, formatXaf } from '../lib/format.js';
import { useMeta } from '../state/MetaContext.jsx';
import { useToast } from '../state/ToastContext.jsx';

/*
 * Refunds: a security deposit handed back at the end of a lease, or what a
 * utility customer paid over their bills. Only the landlord can issue one - a
 * manager sees the figures and the history but not the buttons - and never for
 * more than is held. Every refund is in the money log, and has its own receipt.
 */

const today = () => new Date().toISOString().slice(0, 10);

/**
 * The four figures anybody asks about a bill: what was billed, what has been
 * paid, what is still to pay, and anything paid over. Overpaid money carries
 * the way to refund it, for the landlord.
 */
export const MoneySummary = ({ billed, paid, owed, credit, refunded = 0, overdue = 0, refundTo = null, onRefund = null }) => (
  <div className="stat-row money-summary">
    <StatTile value={formatXaf(billed)} label="Billed" note="Bills sent out" />
    <StatTile value={formatXaf(paid)} label="Paid" note={refunded > 0 ? `${formatXaf(refunded)} of it refunded since` : 'Confirmed receipts'} />
    <StatTile
      value={formatXaf(owed)}
      label="Still to pay"
      note={owed > 0 ? (overdue > 0 ? `${formatXaf(overdue)} of it overdue` : 'Not overdue yet') : 'Nothing owing'}
      tone={owed > 0 ? 'warn' : undefined}
    />
    <StatTile
      value={formatXaf(credit)}
      label="Overpaid"
      note={
        credit > 0 ? (
          onRefund ? (
            <button type="button" className="btn btn-primary btn-sm" onClick={onRefund}>
              Refund it
            </button>
          ) : refundTo ? (
            <Link to={refundTo} className="link-strong">
              Refund it
            </Link>
          ) : (
            'Held as credit until refunded'
          )
        ) : (
          'Nothing paid over'
        )
      }
      tone={credit > 0 ? 'credit' : undefined}
    />
  </div>
);

const RefundModal = ({ kind, subject, available, onDone, onClose }) => {
  const toast = useToast();
  const { paymentMethods } = useMeta();
  const deposit = kind === 'deposit';
  const [form, setForm] = useState({
    amount: String(available),
    withheldReason: '',
    method: 'cash',
    reference: '',
    paidOn: today(),
    note: ''
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  // Proof the money really went out. Required: the tenant is sent it with the receipt.
  const [proof, setProof] = useState(null);
  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const amount = Math.max(0, Math.floor(Number(form.amount) || 0));
  // For a deposit, whatever is not handed back is kept - and has to be explained.
  const withheld = deposit ? Math.max(0, available - amount) : 0;

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      const formData = new FormData();
      const fields = {
        ...subject,
        amount,
        withheld,
        withheldReason: withheld ? form.withheldReason.trim() : '',
        method: form.method,
        reference: form.reference.trim(),
        paidOn: form.paidOn,
        note: form.note.trim()
      };
      for (const [key, value] of Object.entries(fields)) {
        if (value !== '' && value !== null && value !== undefined) formData.append(key, String(value));
      }
      formData.append('proof', proof);
      const { refund } = await api.upload('/landlord/refunds', formData);
      toast.success(`Refund ${refund.refundNumber} recorded. The ${deposit ? 'tenant' : 'customer'} has been told.`);
      onDone(refund);
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={deposit ? 'Refund the security deposit' : 'Refund the overpayment'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="submit"
            form="refund-form"
            className="btn btn-primary"
            disabled={busy || !proof || amount + withheld <= 0 || amount > available}
            title={proof ? undefined : 'Attach the proof of payment first'}
          >
            {busy ? 'Saving...' : amount > 0 ? `Record refund of ${formatXaf(amount)}` : 'Record that it is kept'}
          </button>
        </>
      }
    >
      <p className="modal-lead">
        {deposit
          ? `${formatXaf(available)} of the deposit is held on this lease. Record what you are handing back; anything you keep needs a reason, which the tenant sees.`
          : `This meter account is ${formatXaf(available)} in credit. Record what you are handing back.`}{' '}
        Record it once the money has actually been paid out.
      </p>
      <form id="refund-form" className="stack" onSubmit={submit}>
        <div className="grid-2">
          <Field label="Amount refunded (FCFA)" error={errors.amount} required>
            <input type="number" min="0" max={available} value={form.amount} onChange={(event) => set({ amount: event.target.value })} required />
          </Field>
          <Field label="Paid on" error={errors.paidOn} required>
            <input type="date" max={today()} value={form.paidOn} onChange={(event) => set({ paidOn: event.target.value })} required />
          </Field>
        </div>
        {amount > available ? <div className="notice notice-bad">That is more than the {formatXaf(available)} held.</div> : null}
        {withheld > 0 ? (
          <Field label={`Why are you keeping ${formatXaf(withheld)}?`} error={errors.withheldReason} required>
            <input
              type="text"
              placeholder="For example: broken window pane, unpaid water bill"
              value={form.withheldReason}
              onChange={(event) => set({ withheldReason: event.target.value })}
              required
            />
          </Field>
        ) : null}
        <div className="grid-2">
          <Field label="Paid by" error={errors.method} required>
            <select value={form.method} onChange={(event) => set({ method: event.target.value })}>
              {paymentMethods.map((method) => (
                <option key={method.slug} value={method.slug}>
                  {method.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Reference" hint="The mobile money or bank reference, if any." error={errors.reference}>
            <input type="text" value={form.reference} onChange={(event) => set({ reference: event.target.value })} />
          </Field>
        </div>
        <Field label="Note" hint="Optional. Kept with the refund." error={errors.note}>
          <input type="text" value={form.note} onChange={(event) => set({ note: event.target.value })} />
        </Field>
        <Field
          label="Proof of the refund"
          hint={
            proof
              ? `Attached: ${proof.name}`
              : `A screenshot of the mobile money transfer, the bank slip, or a photo of the note they signed for cash. It is sent to the ${deposit ? 'tenant' : 'customer'} with their receipt.`
          }
          error={errors.proof}
          required
        >
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={(event) => setProof(event.target.files?.[0] || null)}
            required
          />
        </Field>
        {!proof ? <p className="field-hint">The refund can only be recorded once the proof is attached.</p> : null}
      </form>
    </Modal>
  );
};

/** The refunds already made on a lease or meter account, each cancellable by the landlord. */
const RefundList = ({ items, canIssue, onChanged }) => {
  const toast = useToast();
  const [cancelling, setCancelling] = useState(null);
  if (!items.length) return null;

  return (
    <>
      <ul className="refund-list">
        {items.map((refund) => (
          <li key={refund.id} className={refund.voided ? 'is-void' : ''}>
            <div>
              <strong>
                {refund.amount > 0 ? `${formatXaf(refund.amount)} refunded` : 'Deposit kept'}
                {refund.withheld ? `, ${formatXaf(refund.withheld)} kept` : ''}
              </strong>
              <span className="muted">
                {' '}
                {refund.refundNumber} - {formatDate(refund.paidOn)} - {refund.method.replace(/_/g, ' ')}
                {refund.withheldReason ? ` - kept for: ${refund.withheldReason}` : ''}
              </span>
              {refund.voided ? <span className="muted"> Cancelled: {refund.voidReason}</span> : null}
            </div>
            <div className="cell-actions">
              {refund.voided ? <StatusBadge status="withdrawn" label="Cancelled" /> : null}
              <Link to={`/refunds/${refund.id}`} className="btn btn-ghost btn-sm">
                Receipt
              </Link>
              {refund.proofUrl ? (
                <a href={refund.proofUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                  Proof
                </a>
              ) : null}
              {canIssue && !refund.voided ? (
                <button type="button" className="link-btn" onClick={() => setCancelling(refund)}>
                  Cancel
                </button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
      {cancelling ? (
        <ReasonModal
          title={`Cancel refund ${cancelling.refundNumber}?`}
          lead="Use this only if the refund was recorded by mistake or never reached them. It stays on the books marked as cancelled, and the money counts as held again."
          label="Why is it being cancelled?"
          confirmLabel="Cancel the refund"
          tone="danger"
          onSubmit={async (reason) => {
            await api.post(`/landlord/refunds/${cancelling.id}/cancel`, { reason });
            toast.success('Refund cancelled.');
            setCancelling(null);
            onChanged();
          }}
          onClose={() => setCancelling(null)}
        />
      ) : null}
    </>
  );
};

/** On a lease: where the deposit stands, and the landlord's refund button. */
export const DepositPanel = ({ tenancyId, onChanged = () => {} }) => {
  const data = useFetch(`/landlord/refunds?tenancyId=${tenancyId}`, [tenancyId]);
  const [refunding, setRefunding] = useState(false);
  if (data.loading && !data.data) return <Spinner />;
  if (!data.data) return null;
  const { deposit, available, canIssue, items } = data.data;
  if (!deposit.agreed && !deposit.paid && !items.length) return null;

  return (
    <section className="section-block">
      <div className="section-head">
        <h2>Security deposit</h2>
        {canIssue && available > 0 ? (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setRefunding(true)}>
            Refund deposit
          </button>
        ) : null}
      </div>
      <dl className="deposit-figures">
        <div>
          <dt>Agreed</dt>
          <dd>{formatXaf(deposit.agreed)}</dd>
        </div>
        <div>
          <dt>Paid by the tenant</dt>
          <dd>{formatXaf(deposit.paid)}</dd>
        </div>
        <div>
          <dt>Refunded</dt>
          <dd>{formatXaf(deposit.refunded)}</dd>
        </div>
        <div>
          <dt>Kept</dt>
          <dd>{formatXaf(deposit.withheld)}</dd>
        </div>
        <div>
          <dt>Still held</dt>
          <dd>
            <strong>{formatXaf(deposit.held)}</strong>
          </dd>
        </div>
      </dl>
      {!canIssue && available > 0 ? (
        <p className="muted">Only the landlord can refund the deposit, from their own account.</p>
      ) : null}
      <RefundList
        items={items}
        canIssue={canIssue}
        onChanged={() => {
          data.reload();
          onChanged();
        }}
      />
      {refunding ? (
        <RefundModal
          kind="deposit"
          subject={{ tenancyId }}
          available={available}
          onDone={() => {
            setRefunding(false);
            data.reload();
            onChanged();
          }}
          onClose={() => setRefunding(false)}
        />
      ) : null}
    </section>
  );
};

/** On a meter account: any credit, and the landlord's refund button. */
export const UtilityRefundPanel = ({ accountId, openOnLoad = false, onChanged = () => {} }) => {
  const data = useFetch(`/landlord/refunds?accountId=${accountId}`, [accountId]);
  const [refunding, setRefunding] = useState(openOnLoad);
  if (!data.data) return null;
  const { available, canIssue, items } = data.data;
  if (available <= 0 && !items.length) return null;

  return (
    <section className="section-block">
      <div className="section-head">
        <h2>Overpaid</h2>
        {canIssue && available > 0 ? (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setRefunding(true)}>
            Refund {formatXaf(available)}
          </button>
        ) : null}
      </div>
      {available > 0 ? (
        <p>
          This customer has paid <strong>{formatXaf(available)}</strong> more than they were billed. It stays as a credit on
          the account until it is refunded{canIssue ? '' : ' - which only the landlord can do'}.
        </p>
      ) : null}
      <RefundList
        items={items}
        canIssue={canIssue}
        onChanged={() => {
          data.reload();
          onChanged();
        }}
      />
      {refunding && canIssue && available > 0 ? (
        <RefundModal
          kind="utility"
          subject={{ accountId }}
          available={available}
          onDone={() => {
            setRefunding(false);
            data.reload();
            onChanged();
          }}
          onClose={() => setRefunding(false)}
        />
      ) : null}
    </section>
  );
};
